<!-- ダッシュボード トークルーム選択時のページ -->
<script setup lang="ts">
  import { ref } from "vue";
  import { storeToRefs } from "pinia";
  import { useElementBounding } from "@vueuse/core";
  import { usePlanStore } from "~/stores/PlanStore";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";
  import { useTalkUserDataStore } from "~/stores/TalkUserDataStore";
  import { useLoginStore } from "~/stores/LoginStore";
  import { TalkRoom } from "~/entities/TalkRoom";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  import { useCheckContractExpiration } from "~/composables/useCheckContractExpiration";

  const historyStore = useHistoryStore();
  const planStore = usePlanStore();
  const talkRoomStore = useTalkRoomStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const talkUserDataStore = useTalkUserDataStore();
  const loginStore = useLoginStore();

  // 契約期間切れチェック
  // トークは firestore から直接データを読み出しているが契約期間のチェックが入っていないため、
  // 契約期間を満了してもデータを参照できてしまう場合がある。
  // そのため TalkView に対して定期チェックを入れ、契約満了していた場合はリロードしてデータを参照できなくする。
  const checkContractExpiration = useCheckContractExpiration();

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  interface Props {
    planId: string;
    planInfo: PlanInfo;
    isTest?: boolean;
  }
  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: "onPaste"): void;
    (e: "onDrop"): void;
  }>();

  // 権限周り制御
  const isOwner = computed<boolean>(() => props.planInfo.isOwner);

  // 閲覧権限
  const canView = (talkRoom: TalkRoom | undefined) => {
    return canViewTalkRoom(talkRoom, isOwner.value, props.planInfo.isContract);
  };

  // 閲覧権限
  const canViewSelectedRoom = computed<boolean>(() => {
    return canView(selectedTalkRoom.value);
  });

  // 投稿権限
  const canAction = computed<boolean>(() => {
    // アーカイブ済みは投稿不可
    if (selectedTalkRoom.value?.isArchive) {
      return false;
    }
    // DM かつ対象ユーザーが契約終了している場合は投稿不可
    if (selectedDMUserContractEnded.value) {
      return false;
    }

    if (isOwner.value) return true;
    if (!selectedTalkRoom.value) return false;
    if (selectedTalkRoom.value.published) {
      if (selectedTalkRoom.value.isOnlyOwnerPost) {
        // オーナーのみ投稿可能
        return false;
      }
      if (loginStore.isLogin()) {
        return props.planInfo.isContract;
      } else {
        return false;
      }
    } else {
      return false;
    }
  });

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();
  const isSp = isSpDisplayRef();

  // 高さ調整
  const talkRoomRef = ref<HTMLDivElement | undefined>(undefined);

  const talkRoomHeight = computed<string | null>(() => {
    if (isTablet.value && isTalkRoomListView.value) return "inherit";
    const rect = talkRoomRef.value?.getBoundingClientRect();
    if (rect) return `calc(100svh - ${rect.y}px)`;
    return null;
  });

  // リストエリアの高さ
  const listAreaElementHeight = computed<string | null>(() => {
    const rect = talkRoomRef.value?.getBoundingClientRect();
    if (rect) return `calc(100svh - ${rect.y + 50}px)`; // talkRoomHeight より一覧タブの分小さめに
    return null;
  });

  // ヘッダーの高さによってメッセージエリアの高さを調整する
  const headerAreaRef = ref<HTMLDivElement | undefined>(undefined);
  const { width: headerAreaElementWidth, height: headerAreaElementHeight } = useElementBounding(headerAreaRef);
  const messageAreaHeight = computed<string>(() => {
    if (headerAreaElementHeight.value) return `calc(100% - ${headerAreaElementHeight.value}px)`;
    return "calc(100% - 60px)";
  });

  const detailMessagesRef = ref<HTMLDivElement | undefined>(undefined);

  // SP時にモード管理
  const isTalkRoomListView = ref(true);

  const { talkRooms } = storeToRefs(talkRoomStore);
  const { searchOpen } = storeToRefs(talkRoomTmpViewStore);

  // セーフエリア取得
  const safeArea = useScreenSafeArea();

  // インプットエリアの高さ
  const inputAreaRef = ref<HTMLDivElement | undefined>(undefined);
  const { height: inputAreaElementHeight } = useElementBounding(inputAreaRef);
  const inputAreaHeight = computed<string>(() => {
    const baseHeight = inputAreaElementHeight.value ? inputAreaElementHeight.value : 60;
    if (safeArea.bottom.value) {
      return `calc(${baseHeight}px + ${safeArea.bottom.value})`; // セーフエリアを加算して返す
    } else {
      return `calc(${baseHeight}px)`; // セーフエリアなし
    }
  });

  // メッセージ表示可否
  const canShowMessages = computed<boolean>(() => {
    // ルームが参照可能かつエリアサイズが確定してから表示させる (でないとメッセージ側の初期化が不安定になる)
    return canViewSelectedRoom.value && !!headerAreaElementHeight.value && !!messageAreaHeight.value;
  });

  const selectedTalkRoom = ref<TalkRoom>();

  const changeTalkRoomById = (roomId: string) => {
    const talkRoom = talkRooms.value.find(item => item.talkRoomId === roomId);
    if (talkRoom) {
      changeTalkRoom(talkRoom);
    }
  };
  const changeTalkRoom = (talkRoom: TalkRoom) => {
    selectedTalkRoom.value = talkRoom;
    isTalkRoomListView.value = false;
  };

  // DM 対象ユーザーの契約情報
  const selectedDMUserContractEnded = ref<boolean>(false);
  watch(selectedTalkRoom, async talkRoom => {
    // 現在のルームが DM かつ自分が講師の場合、対象ユーザーの契約終了をチェック
    if (talkRoom?.isDirectMessage && isOwner.value) {
      const userId = talkRoom.dmUser!.userId;
      const contract = await planStore.fetchMember(props.planId, userId);
      if (isContractExpired(contract)) {
        // 対象ユーザーが契約終了している場合に投稿不可にするフラグ・snackbarをセット
        // (そのユーザー自身は契約終了している場合は未入会状態でそもそも投稿不可)
        selectedDMUserContractEnded.value = true;
        snackBarStore.setError(
          "このユーザーは講座解約済みのため投稿できません。再入会された場合、再度投稿可能となります。",
        );
      } else {
        // 上記以外のケースはフラグ・snackbarを落とす
        selectedDMUserContractEnded.value = false;
        snackBarStore.show = false;
      }
    } else {
      // 上記以外のケースはフラグ・snackbarを落とす
      selectedDMUserContractEnded.value = false;
      snackBarStore.show = false;
    }
  });

  const backToListView = () => {
    historyStore.to(`/plan/${props.planId}?tab=talk`);
  };

  // DM 遅延取得中フラグ
  const isLoadingDm = ref(false);

  const getTalkRooms = async () => {
    if (isOwner.value) {
      // 講師の場合、まずはチャンネルリストのみ即時取得し、DMは遅延取得する
      // そうすることで初回描画を高速化する（講師の場合にDMが大量にあると未読チェックが重いため）

      // まずはチャンネルリストを取得
      await talkRoomStore.getTalkRooms(props.planId, true);

      // その後 DM は非同期で遅延取得
      isLoadingDm.value = true;
      talkRoomStore.getTalkRooms(props.planId).finally(() => {
        isLoadingDm.value = false;
      });
    } else {
      await talkRoomStore.getTalkRooms(props.planId);
    }
  };

  // 検索関連
  const isJumping = ref(false);
  const searchIdx = ref<number | null>(null);
  const searchTalkId = ref<string | null>(null);
  const searchWord = ref<string | null>(null);
  const searchTop = computed(() => headerAreaElementHeight.value + (isTablet.value ? 0 : 134));
  const searchLeft = computed(() => (isTablet.value ? 0 : 248));
  const searchWidth = computed(() => {
    if (!headerAreaElementWidth.value) {
      return undefined;
    }
    return headerAreaElementWidth.value;
  });

  const scrollToLatestTalk = () => {
    (detailMessagesRef.value! as any).scrollToBottom();
  };

  // サブコンポーネントで発生するエラーを適宜捕捉
  onErrorCaptured(error => {
    // VImg での謎のエラーを無視する https://fincs.backlog.com/view/FINCS-2557#comment-428277759
    if (
      error?.name === "TypeError" &&
      (error?.message?.startsWith("Cannot destructure property 'naturalHeight' of") ||
        error?.message?.startsWith("Right side of assignment cannot be destructured"))
    ) {
      sentryWarnLog(`TalkView.onErrorCaptured: ${error}`); // warn だけ記録
      return false; // propagate しない
    }
  });

  // queryのroomの監視
  const route = useRoute();
  watch(
    () => route.query.room,
    () => {
      if (route.query.room && initTalkRooms.value) {
        changeTalkRoomById(route.query.room as string);
      } else if (!route.query.room) {
        // ルーム指定がない場合、リストビューへ戻した後デフォルトルーム設定
        selectedTalkRoom.value = undefined;
        isTalkRoomListView.value = true;
        changeTalkRoomDefault();
      }
    },
  );

  // 初期化したかどうか
  const initTalkRooms = ref(false);

  const changeTalkRoomDefault = () => {
    if (!isTablet.value && talkRooms.value.length > 0) {
      // PCだったら選択済み

      // 最優先はチャンネル
      const groupTalkRooms = talkRooms.value.filter(room => !room.isArchive && !room.isDirectMessage);
      if (groupTalkRooms.length > 0) {
        changeTalkRoom(groupTalkRooms[0]);
        return;
      }
      // 次にDM
      const dMTalkRooms = talkRooms.value.filter(room => !room.isArchive && room.isDirectMessage);
      if (dMTalkRooms.length > 0) {
        changeTalkRoom(dMTalkRooms[0]);
        return;
      }
      // 最後にアーカイブ
      changeTalkRoom(talkRooms.value[0]);
    }
  };

  const fetch = async () => {
    if (initTalkRooms.value) {
      return;
    }
    talkRoomStore.initPlan(props.planId);
    talkUserDataStore.initPlan(props.planId);

    await getTalkRooms();
    talkRoomStore.startTalkRoomsObserve(props.planId);
    talkRoomStore.startUsersObserve(props.planId);

    if (route.query.room) {
      // queryでルームの指定があったらそのルームを表示する
      changeTalkRoomById(route.query.room as string);
    } else if (!selectedTalkRoom.value) {
      changeTalkRoomDefault();
    }
    initTalkRooms.value = true;
  };

  onMounted(() => {
    checkContractExpiration.onMounted(props.planId);
    fetch();
  });

  onUnmounted(() => {
    checkContractExpiration.onUnmounted();
    talkRoomStore.stopUsersObserve();
    talkRoomStore.stopTalkRoomsObserve();
  });

  // ファイルドラッグ＆ドロップ、ペースト時の処理
  const onFiles = (files: FileList) => {
    if (inputAreaRef.value) {
      (inputAreaRef.value as any).onDrop(files);
    }
  };
  const onDrop = () => {
    emit("onDrop");
  };
  const onPaste = () => {
    emit("onPaste");
  };

  const tab = ref("talk-rooms");
  const hasThreadList = featureTalkThread();
  defineExpose({
    fetch,
  });
</script>

<template>
  <div v-if="!initTalkRooms"></div>
  <div
    v-else
    ref="talkRoomRef"
    class="talkroom_wrapper"
    :class="{ isTalkRoomListView: isTalkRoomListView, isTalkRoomDetailView: !isTalkRoomListView }"
  >
    <!-- 左ペイン -->
    <div :class="isSp ? `talkroom_left-sp` : `talkroom_left`">
      <v-tabs v-model="tab" fixed-tabs selected-class="talkroom_tab_selected">
        <v-tab value="talk-rooms" style="min-width: 60px">
          <v-icon icon="$mdiHome"></v-icon>
        </v-tab>
        <v-tab v-if="hasThreadList" value="talk-threads" style="min-width: 60px" data-testid="talk-threads-tab">
          <v-icon icon="$mdiMessageText"></v-icon>
        </v-tab>
        <v-tab value="talk-bookmarks" style="min-width: 60px" data-testid="talk-bookmarks-tab">
          <v-icon icon="$mdiBookmark"></v-icon>
        </v-tab>
        <v-tab value="talk-mentions" style="min-width: 60px" data-testid="talk-mentions-tab">
          <v-icon icon="$mdiAt"></v-icon>
        </v-tab>
      </v-tabs>
      <v-window v-model="tab">
        <!-- トークルーム一覧 -->
        <v-window-item value="talk-rooms">
          <OrganismsTalkRoomList
            :plan-info="planInfo"
            :talk-rooms="talkRooms"
            :selected-talk-room="selectedTalkRoom"
            :is-loading-dm="isLoadingDm"
          />
        </v-window-item>

        <!-- トークスレッド一覧 -->
        <v-window-item v-if="hasThreadList" value="talk-threads">
          <OrganismsTalkThreadList
            v-if="listAreaElementHeight"
            :plan-info="planInfo"
            :login-info="loginStore.loginInfo"
            :height="listAreaElementHeight"
          />
        </v-window-item>

        <!-- トークブックマーク一覧 -->
        <v-window-item value="talk-bookmarks">
          <OrganismsTalkBookmarkList
            v-if="listAreaElementHeight"
            :plan-info="planInfo"
            :login-info="loginStore.loginInfo"
            :height="listAreaElementHeight"
          />
        </v-window-item>

        <!-- トークメンション一覧 -->
        <v-window-item value="talk-mentions">
          <OrganismsTalkMentionList
            v-if="listAreaElementHeight"
            :plan-info="planInfo"
            :login-info="loginStore.loginInfo"
            :height="listAreaElementHeight"
          />
        </v-window-item>
      </v-window>
    </div>

    <!-- トークルーム詳細 -->
    <!-- ファイルドラッグ＆ドロップ、ペーストを処理 -->
    <AtomsFileDrop
      class="talkroom_detail"
      data-testid="talkroom-detail"
      :can-action="canAction"
      :is-test="isTest"
      @files="onFiles"
      @on-drop="onDrop"
      @on-paste="onPaste"
    >
      <div v-if="talkRooms.length < 1 && !isSp" class="mx-8 my-10">チャンネルがありません。</div>
      <template v-else-if="selectedTalkRoom">
        <!-- ヘッダーエリア -->
        <OrganismsTalkRoomDetailHeader
          ref="headerAreaRef"
          v-model:is-searching="searchOpen"
          :plan-info="planInfo"
          :selected-talk-room="selectedTalkRoom"
          :is-owner="isOwner"
          :back-to-list-view="backToListView"
        />

        <!-- 検索エリア -->
        <OrganismsTalkRoomDetailSearch
          v-if="searchWidth"
          v-model:is-searching="searchOpen"
          :is-jumping="isJumping"
          :talk-room-id="selectedTalkRoom.talkRoomId"
          :top-px="searchTop"
          :left-px="searchLeft"
          :width-px="searchWidth"
          @search-idx="searchIdx = $event"
          @search-talk-id="searchTalkId = $event"
          @search-word="searchWord = $event"
        />

        <!-- メッセージエリア -->
        <MoleculesContentsNoPermissionDialog
          v-if="!canViewSelectedRoom"
          :plan-id="planId"
          :plan-title="planInfo.title"
          content-type="トーク"
          @click-bg="() => (isSp ? backToListView() : undefined)"
        />
        <OrganismsTalkRoomDetailMessages
          v-else-if="canShowMessages"
          ref="detailMessagesRef"
          :plan-info="planInfo"
          :selected-talk-room="selectedTalkRoom"
          :can-action="canAction"
          :search-idx="searchIdx"
          :search-talk-id="searchTalkId"
          :search-word="searchWord"
          :height="messageAreaHeight"
          :padding-bottom="inputAreaHeight"
          @is-jumping="isJumping = $event"
        ></OrganismsTalkRoomDetailMessages>

        <!-- 入力エリア -->
        <OrganismsTalkRoomInput
          ref="inputAreaRef"
          :talk-room="selectedTalkRoom"
          :can-action="canAction"
          @send="scrollToLatestTalk"
        ></OrganismsTalkRoomInput>
      </template>
    </AtomsFileDrop>

    <OrganismsTalkViewInputRestore />
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .talkroom_wrapper {
    max-width: 1280px;
    height: v-bind("talkRoomHeight");
    display: table;
    position: relative;
    width: 100%;
    @include display_pc {
    }
    @include display_sp {
    }
    .talkroom_left {
      width: 248px;
    }
    .talkroom_left_sp {
      width: 100%;
    }
    .talkroom_tab_selected {
      background-color: rgb(22, 22, 22);
    }
    .talkroom_detail {
      display: table-cell;
      width: 100%;
      height: 100%;
      vertical-align: top;
      position: relative;
    }
    .infinite-status-prompt {
      display: none;
    }
  }
  @include display_tablet {
    .talkroom_wrapper {
      width: 100vw;
      height: initial !important;
      &.isTalkRoomListView {
        .talkroom_detail {
          display: none;
        }
      }
      &.isTalkRoomDetailView {
        .talkroom_detail {
          position: fixed;
          width: 100vw;
          height: 100svh;
          z-index: 1000;
          top: 0;
          left: 0;
          background: black;
        }
      }
    }
  }
</style>
